import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { type ReactElement, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { WBSButton } from '@/common/WBSButton';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useCoordinatorGroupAccess } from '~/hooks/useIsCoordinatorGroup';
import { useIsGroupAdmin } from '~/hooks/useIsGroupAdmin';
import { useLanguageLabels } from '~/hooks/useLanguageLabels';
import { AvatarNameCell } from '~/modules/tables/AvatarNameCell';
import { useWbsTableQuery } from '~/modules/tables/useWbsTableQuery';
import WbsTable, { type Column } from '~/modules/tables/WbsTable';
import { GroupHeaderWrapper, Title } from '~/styles/layoutElements';
import type { FilterField } from '~/styles/tables/filterandSettingsStyles';
import { formatLongDate } from '~/utils/dateFormat';
import { apiLanguageToLocale } from '~/utils/languageFormat';

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/group-members/',
)({
  component: RouteComponent,
});

/** Keys this table's remembered filters and column visibility. */
const TABLE_KEY = 'group-members';

type GroupMemberItem = components['schemas']['GroupMemberItem'];

/**
 * `Column<T>` only accepts keys of the row type, and the API item has no
 * `actions` (the cell is built from the viewer's own id), so the row gets one
 * synthetic key. Intersecting the generated type keeps `tsc` failing if the
 * payload renames a field.
 */
type RosterRow = GroupMemberItem & { actions: null };

const toRosterRow = (item: GroupMemberItem): RosterRow => ({
  ...item,
  actions: null,
});

/**
 * A text box yields a string; the endpoint wants an int and 422s on junk.
 *
 * Non-numeric input is dropped silently, so the panel shows the typed value
 * over an unfiltered roster. The fix is a numeric field type in the shared
 * filter panel, which would cover the sibling `student_id` filter too.
 */
const toMemberId = (value: unknown): number | undefined => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};

function RouteComponent(): ReactElement | null {
  const { t } = useTranslation();
  const { groupName } = Route.useParams();
  const navigate = useNavigate();
  const { me } = useCurrentUser();
  const memberId = String(me?.id ?? '');
  const languageLabels = useLanguageLabels();

  // Gated on group type only, like the sidebar link that offers this route.
  // `isResolved` matters: a bare `!isCoordinatorGroup` bounces every caller,
  // the group's coordinator included, before `getGroupBySlug` lands.
  const { isCoordinatorGroup, isResolved } = useCoordinatorGroupAccess();
  const refused = isResolved && !isCoordinatorGroup;
  // The viewer's role on this group, for the two drill-in links below: this tab
  // admits a mod, but the Students tab they link into is admin-only, so a mod's
  // link would bounce.
  const isGroupAdmin = useIsGroupAdmin();

  useEffect(() => {
    if (refused) {
      navigate({
        to: '/groups/$groupName/',
        params: { groupName },
        replace: true,
      });
    }
  }, [refused, groupName, navigate]);

  const mapFilters = useCallback(
    (filters: Record<string, unknown>): Record<string, unknown> => ({
      ...filters,
      member_id: toMemberId(filters.member_id),
    }),
    [],
  );

  const table = useWbsTableQuery({ tableKey: TABLE_KEY, mapFilters });

  const { data: groupData } = api.getGroupBySlug.useQuery({
    path: { group_slug: groupName },
  });
  const groupId = groupData?.id ?? 0;

  const {
    data: memberData,
    isFetching,
    refetch,
  } = api.getGroupStudyHelpers.useQuery(
    { path: { group_id: groupId }, query: table.params },
    { enabled: groupId > 0 },
  );

  const members = useMemo(
    () => (memberData?.data ?? []).map(toRosterRow),
    [memberData],
  );

  // Declared client-side, not read from `GET /v3/lists/{key}/filters`: there's
  // no list key for this table, since registering one would publish a new table
  // on the fullapp datatables surface. Legacy labels the name filter plain
  // "Name". The keys are the endpoint's query params, which `useWbsTableQuery`
  // sends verbatim.
  const filterFields: FilterField[] = useMemo(
    () => [
      {
        key: 'display_name',
        label: t('wbs.name'),
        type: 'text',
      },
      {
        key: 'member_id',
        label: t('profile.user-id'),
        type: 'text',
      },
    ],
    [t],
  );

  // `sortable` matches the endpoint's four sort keys. The column key is sent as
  // `sort` and the server silently ignores an unknown one, so a typo gives a
  // sort icon that does nothing. In server mode the table never sorts locally.
  const columns: Column<RosterRow>[] = [
    {
      key: 'id',
      title: t('wbs.id'),
      sortable: true,
    },
    {
      key: 'display_name',
      title: t('wbs.name'),
      sortable: true,
      // `linkUserId` makes the avatar and name a profile link, as in legacy;
      // without it `AvatarNameCell` renders a plain span.
      render: (_, member) => (
        <AvatarNameCell
          avatar={member.avatar}
          name={member.display_name}
          linkUserId={member.id}
        />
      ),
      minWidth: '40px',
    },
    {
      key: 'role',
      title: t('groups.group-role'),
      // Legacy's labels: admin is Coordinator, mod is Study Helper, anything
      // else "n/a". They come from the catalogue because legacy's
      // per-group-type labels weren't migrated, so one pair serves both group
      // types.
      //
      // The "n/a" branch is reachable: the admin app adds study helpers with
      // the `member` role, and the roster lists those rows.
      render: (role) =>
        role === 'admin'
          ? t('wbs.coordinator')
          : role === 'mod'
            ? t('lessons.study-helper')
            : t('wbs.n-a-2'),
    },
    {
      key: 'students_adopted',
      title: t('lessons.students-adopted'),
      width: '110px',
      // Both counts drill into the students lists filtered to this helper, but
      // only for a viewer who is a group admin, as in legacy; anyone else sees
      // the bare number.
      render: (value, member) => (
        <RedValue>
          {isGroupAdmin ? (
            <Link
              to="/groups/$groupName/students/all-students/"
              params={{ groupName }}
              search={{ study_helper_id: member.id }}
            >
              {value}
            </Link>
          ) : (
            value
          )}
        </RedValue>
      ),
    },
    {
      key: 'students_waiting',
      title: t('lessons.students-waiting'),
      width: '110px',
      // The destination's total won't match this number, as in legacy: this
      // counts gradings, while the board counts students across five
      // categories.
      render: (value, member) => (
        <RedValue>
          {isGroupAdmin ? (
            <Link
              to="/groups/$groupName/students/students-waiting/"
              params={{ groupName }}
              search={{ study_helper_id: member.id }}
            >
              {value}
            </Link>
          ) : (
            value
          )}
        </RedValue>
      ),
    },
    {
      key: 'gender',
      title: t('wbs.gender'),
      // Four branches. The fourth, for a null gender, is legacy's "n/a" and is
      // distinct from "Prefer Not to Answer", so don't fold it into `unknown`.
      render: (gender) =>
        gender === 'male'
          ? t('wbs.male')
          : gender === 'female'
            ? t('wbs.female')
            : gender === 'unknown'
              ? t('wbs.prefer-not-to-answer')
              : t('wbs.n-a-2'),
    },
    {
      key: 'location',
      title: t('wbs.location'),
      render: (_, member) => (
        <>
          {member.location}
          <br />
          {member.country}
        </>
      ),
    },
    {
      key: 'languages',
      // The languages the helper teaches in, not the account's UI language.
      title: t('wbs.languages'),
      render: (languages) =>
        languages
          .map(
            (language) =>
              languageLabels[apiLanguageToLocale(language)] ?? language,
          )
          .join(', '),
    },
    {
      key: 'last_active',
      title: t('wbs.last-active'),
      sortable: true,
      // `''` for helpers whose legacy last-active date was zero;
      // `formatLongDate` renders an em dash.
      render: (value) => formatLongDate(value),
    },
    {
      key: 'registered',
      title: t('wbs.registered'),
      sortable: true,
      minWidth: '130px',
      render: (value) => formatLongDate(value),
    },
    {
      key: 'actions',
      title: t('wbs.actions'),
      sortable: false,
      render: (_, member) => {
        return (
          <WBSButton
            onClick={() =>
              // `memberId` is the viewer's id and `r` is the row's, as legacy
              // builds the link.
              navigate({
                to: '/members/$memberId/messages/compose/',
                params: { memberId },
                search: { r: Number(member.id) },
              })
            }
          >
            {t('wbs.message')}
          </WBSButton>
        );
      },
    },
  ];

  if (refused) return null;

  return (
    <div>
      <GroupHeaderWrapper>
        <Title>{t('profile.members')}</Title>
      </GroupHeaderWrapper>
      {/* An empty roster uses `WbsTable`'s own empty state. Legacy answered
         an empty result with an error envelope at HTTP 200; v3 returns
         `data: []`. */}
      <WbsTable
        {...table.tableProps}
        data={members}
        columns={columns}
        totalCount={memberData?.count}
        loading={isFetching}
        onRefresh={() => {
          void refetch();
        }}
        filterFields={filterFields}
        searchPlaceholder={t('wbs.search-by-name-or')}
        showSearch
        showFilter
        showSettings
        showRefresh
        cellStyle={{ padding: '14px 8px' }}
        // As in legacy, gender and location are off by default. Unlike legacy,
        // Actions still gets a visibility toggle, since `ColumnSettingsPanel`
        // shows one for every column.
        hiddenColumns={['gender', 'location']}
      />
    </div>
  );
}

const RedValue = styled.span`
  color: var(--wbs-red);
`;
